export type FontGroup = 'Sans' | 'Display' | 'Serif' | 'Script' | 'Mono' | 'Indian';

export interface FontDef {
  family: string;
  weights: number[];
  group: FontGroup;
}

/** Fonts offered in the Studio (Google Fonts), including Devanagari for Hindi and Marathi. */
export const FONTS: FontDef[] = [
  { family: 'Poppins', weights: [400, 600, 700, 800], group: 'Sans' },
  { family: 'Montserrat', weights: [400, 600, 800], group: 'Sans' },
  { family: 'Inter', weights: [400, 600, 800], group: 'Sans' },
  { family: 'Raleway', weights: [400, 700], group: 'Sans' },
  { family: 'Nunito', weights: [400, 800], group: 'Sans' },
  { family: 'Rubik', weights: [400, 700], group: 'Sans' },
  { family: 'Oswald', weights: [400, 700], group: 'Display' },
  { family: 'Bebas Neue', weights: [400], group: 'Display' },
  { family: 'Anton', weights: [400], group: 'Display' },
  { family: 'Archivo Black', weights: [400], group: 'Display' },
  { family: 'Righteous', weights: [400], group: 'Display' },
  { family: 'Abril Fatface', weights: [400], group: 'Display' },
  { family: 'Playfair Display', weights: [400, 700, 900], group: 'Serif' },
  { family: 'Merriweather', weights: [400, 900], group: 'Serif' },
  { family: 'Lora', weights: [400, 700], group: 'Serif' },
  { family: 'Roboto Slab', weights: [400, 700], group: 'Serif' },
  { family: 'Dancing Script', weights: [400, 700], group: 'Script' },
  { family: 'Pacifico', weights: [400], group: 'Script' },
  { family: 'Great Vibes', weights: [400], group: 'Script' },
  { family: 'Lobster', weights: [400], group: 'Script' },
  { family: 'Permanent Marker', weights: [400], group: 'Script' },
  { family: 'Kalam', weights: [400, 700], group: 'Script' },
  { family: 'Courier Prime', weights: [400, 700], group: 'Mono' },
  { family: 'Noto Sans Devanagari', weights: [400, 700], group: 'Indian' },
  { family: 'Mukta', weights: [400, 700], group: 'Indian' },
  { family: 'Hind', weights: [400, 700], group: 'Indian' },
  { family: 'Baloo 2', weights: [400, 700], group: 'Indian' },
  { family: 'Yatra One', weights: [400], group: 'Indian' },
  { family: 'Teko', weights: [400, 600], group: 'Indian' },
];

let sheet: Promise<void> | null = null;

/** Adds the Google Fonts stylesheet once (font files load only when used). */
function loadSheet(): Promise<void> {
  if (!sheet) {
    sheet = new Promise<void>((resolve) => {
      const families = FONTS.map((f) => `family=${f.family.replace(/ /g, '+')}:wght@${f.weights.join(';')}`).join('&');
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
      link.onload = () => resolve();
      link.onerror = () => resolve();
      setTimeout(resolve, 4000);
      document.head.appendChild(link);
    });
  }
  return sheet;
}

/** Waits until these families (all weights) can be drawn on a canvas. */
export async function loadFonts(families: Array<string | null | undefined>): Promise<void> {
  await loadSheet();
  const wanted = [...new Set(families.filter((f): f is string => !!f))];
  try {
    await Promise.all(wanted.flatMap((fam) => {
      const f = FONTS.find((x) => x.family === fam);
      return (f ? f.weights : [400, 700]).map((w) => document.fonts.load(`${w} 40px "${fam}"`));
    }));
  } catch { /* offline: system fonts are used */ }
}

/** Every font family used in a Fabric JSON (pages or objects). */
export function fontsIn(json: string | object): string[] {
  const out = new Set<string>();
  const walk = (o: unknown): void => {
    if (!o || typeof o !== 'object') return;
    const rec = o as Record<string, unknown>;
    if (typeof rec.fontFamily === 'string') out.add(rec.fontFamily);
    Object.values(rec).forEach(walk);
  };
  walk(typeof json === 'string' ? JSON.parse(json) : json);
  return [...out];
}

/** Kept for the parts of the Studio that want a few common fonts ready. */
export const ensureFonts = (): Promise<void> =>
  loadFonts(['Poppins', 'Montserrat', 'Playfair Display', 'Bebas Neue', 'Dancing Script', 'Noto Sans Devanagari', 'Courier Prime']);